import Head from 'next/head'
import React, { useState, useEffect } from 'react'

export default function Terms() {
  return (
    <>
      <Head>
        <title>Terms of Service - OpenSpeech</title>
      </Head>
      {/* Header section */}
      <div className="px-6 pt-16 lg:px-8">
        <h1 className="mx-auto max-w-4xl text-center text-5xl font-bold tracking-tight">
          OpenSpeech Terms of Service: Your Guide to Our Policies.
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-center leading-8 text-gray-600">
          Get to know the guidelines that govern our services at OpenSpeech. Our
          Terms of Service page outlines your rights and responsibilities as a
          user, providing a comprehensive understanding of our policies and
          ensuring a trusted, secure environment.{' '}
        </p>
      </div>

      {/* Main content section */}
      <div className="px-6 py-16 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold tracking-tight">
            Terms of Service
          </h2>
          <p className="mt-2 text-gray-600">Last updated: 2026-08-28</p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            User Obligations
          </h2>

          <p className="mt-2 text-gray-600">
            You agree to use the Service only for purposes that are permitted by
            (a) the Terms and (b) any applicable law, regulation or generally
            accepted practices or guidelines in the relevant jurisdictions
            (including any laws regarding the export of data or software to and
            from the United States or other relevant countries).
          </p>

          <p className="mt-2 text-gray-600">
            You agree not to access (or attempt to access) any of the Service by
            any means other than through the interface that is provided by
            OpenSpeech, unless you have been specifically allowed to do so in a
            separate agreement with OpenSpeech. You specifically agree not to
            access (or attempt to access) any of the Service through any
            automated means (including use of scripts or web crawlers) and shall
            ensure that you comply with the instructions set out in any
            robots.txt file present on the Service.
          </p>

          <p className="mt-2 text-gray-600">
            You agree that you will not engage in any activity that interferes
            with or disrupts the Service (or the servers and networks which are
            connected to the Service). Unless you have been specifically
            permitted to do so in a separate agreement with OpenSpeech, you
            agree that you will not reproduce, duplicate, copy, sell, trade or
            resell the Service for any purpose.
          </p>

          <p className="mt-2 text-gray-600">
            You agree that you are solely responsible for (and that OpenSpeech
            has no responsibility to you or to any third party for) any breach
            of your obligations under the Terms and for the consequences
            (including any loss or damage which OpenSpeech may suffer) of any
            such breach.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Your Passwords and Account Security
          </h2>

          <p className="mt-2 text-gray-600">
            You agree and understand that you are responsible for maintaining
            the confidentiality of passwords associated with any account you use
            to access the Services.
          </p>

          <p className="mt-2 text-gray-600">
            Accordingly, you agree that you will be solely responsible to
            OpenSpeech for all activities that occur under your account.
          </p>

          <p className="mt-2 text-gray-600">
            If you become aware of any unauthorized use of your password or of
            your account, you agree to notify OpenSpeech immediately at.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Privacy and Your Personal Information
          </h2>

          <p className="mt-2 text-gray-600">
            For information about OpenSpeech’s data protection practices, please
            read OpenSpeech’s privacy policy at{' '}
            <a href="https://openspeech.org/privacy" className="text-blue-500">
              https://openspeech.org/privacy
            </a>
            . This policy explains how OpenSpeech treats your personal
            information, and protects your privacy, when you use the Services.
          </p>

          <p className="mt-2 text-gray-600">
            You agree to the use of your data in accordance with OpenSpeech’s
            privacy policies.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Content in the Services
          </h2>

          <p className="mt-2 text-gray-600">
            You understand that all information (such as data files, written
            text, computer software, music, audio files or other sounds,
            photographs, videos or other images) which you may have access to as
            part of, or through your use of, the Services are the sole
            responsibility of the person from which such content originated. All
            such information is referred to below as the “Content”.
          </p>

          <p className="mt-2 text-gray-600">
            You should be aware that Content presented to you as part of the
            Services, including but not limited to advertisements in the
            Services and sponsored Content within the Services may be protected
            by intellectual property rights which are owned by the sponsors or
            advertisers who provide that Content to OpenSpeech (or by other
            persons or companies on their behalf). You may not modify, rent,
            lease, loan, sell, distribute or create derivative works based on
            this Content (either in whole or in part) unless you have been
            specifically told that you may do so by OpenSpeech or by the owners
            of that Content, in a separate agreement.
          </p>

          <p className="mt-2 text-gray-600">
            OpenSpeech reserves the right (but shall have no obligation) to
            pre-screen, review, flag, filter, modify, refuse or remove any or
            all Content from any Service. For some of the Services, OpenSpeech
            may provide tools to filter out explicit sexual content. These tools
            include the SafeSearch preference settings (see{' '}
            <a
              href="https://support.google.com/websearch/answer/510?hl=en"
              className="text-blue-500"
            >
              https://support.google.com/websearch/answer/510?hl=en
            </a>
            ). In addition, there are commercially available services and
            software to limit access to material that you may find
            objectionable.
          </p>

          <p className="mt-2 text-gray-600">
            You understand that by using the Services you may be exposed to
            Content that you may find offensive, indecent or objectionable and
            that, in this respect, you use the Services at your own risk.
          </p>

          <p className="mt-2 text-gray-600">
            You agree that you are solely responsible for (and that OpenSpeech
            has no responsibility to you or to any third party for) any Content
            that you create, transmit or display while using the Services and
            for the consequences of your actions (including any loss or damage
            which OpenSpeech may suffer) by doing so.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Proprietary Rights
          </h2>

          <p className="mt-2 text-gray-600">
            You acknowledge and agree that OpenSpeech (or OpenSpeech’s
            licensors) own all legal right, title and interest in and to the
            Services, including any intellectual property rights which subsist
            in the Services (whether those rights happen to be registered or
            not, and wherever in the world those rights may exist). You further
            acknowledge that the Services may contain information which is
            designated confidential by OpenSpeech and that you shall not
            disclose such information without OpenSpeech’s prior written
            consent.
          </p>

          <p className="mt-2 text-gray-600">
            Unless you have agreed otherwise in writing with OpenSpeech, nothing
            in the Terms gives you a right to use any of OpenSpeech’s trade
            names, trademarks, service marks, logos, domain names, and other
            distinctive brand features.
          </p>

          <p className="mt-2 text-gray-600">
            If you have been given an explicit right to use any of these brand
            features in a separate written agreement with OpenSpeech, then you
            agree that your use of such features shall be in compliance with
            that agreement, any applicable provisions of the Terms, and
            OpenSpeech’s brand feature use guidelines as updated from time to
            time.
          </p>

          <p className="mt-2 text-gray-600">
            Other than the limited license set forth in Section 7, OpenSpeech
            acknowledges and agrees that it obtains no right, title or interest
            from you (or your licensors) under these Terms in or to any Content
            that you submit, post, transmit or display on, or through, the
            Services, including any intellectual property rights which subsist
            in that Content (whether those rights happen to be registered or
            not, and wherever in the world those rights may exist). Unless you
            have agreed otherwise in writing with OpenSpeech, you agree that you
            are responsible for protecting and enforcing those rights and that
            OpenSpeech has no obligation to do so on your behalf.
          </p>

          <p className="mt-2 text-gray-600">
            You agree that you shall not remove, obscure, or alter any
            proprietary rights notices (including copyright and trade mark
            notices) which may be affixed to or contained within the Services.
          </p>

          <p className="mt-2 text-gray-600">
            Unless you have been expressly authorized to do so in writing by
            OpenSpeech, you agree that in using the Services, you will not use
            any trade mark, service mark, trade name, logo of any company or
            organization in a way that is likely or intended to cause confusion
            about the owner or authorized user of such marks, names or logos.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            License from OpenSpeech
          </h2>

          <p className="mt-2 text-gray-600">
            OpenSpeech gives you a personal, worldwide, royalty-free,
            non-assignable and non-exclusive license to use the software
            provided to you by OpenSpeech as part of the Services as provided to
            you by OpenSpeech (referred to as the “Software” below). This
            license is for the sole purpose of enabling you to use and enjoy the
            benefit of the Services as provided by OpenSpeech, in the manner
            permitted by the Terms.
          </p>

          <p className="mt-2 text-gray-600">
            You may not (and you may not permit anyone else to) copy, modify,
            create a derivative work of, reverse engineer, decompile or
            otherwise attempt to extract the source code of the Software or any
            part thereof, unless this is expressly permitted or required by law,
            or unless you have been specifically told that you may do so by
            OpenSpeech, in writing.
          </p>

          <p className="mt-2 text-gray-600">
            Unless OpenSpeech has given you specific written permission to do
            so, you may not assign (or grant a sub-license of) your rights to
            use the Software, grant a security interest in or over your rights
            to use the Software, or otherwise transfer any part of your rights
            to use the Software.
          </p>

          <p className="mt-2 text-gray-600">
            Software updates, which may include bug fixes, enhanced functions,
            new software modules and completely new versions of the Software,
            may be automatically downloaded and installed and you hereby agree
            to such automatic updating.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Ending your relationship with OpenSpeech
          </h2>

          <p className="mt-2 text-gray-600">
            The Terms will continue to apply until terminated by either you or
            OpenSpeech as set out below.
          </p>

          <p className="mt-2 text-gray-600">
            If you want to terminate your legal agreement with OpenSpeech, you
            may do so by (a) notifying OpenSpeech at any time and (b) closing
            your accounts for all of the Services which you use, where
            OpenSpeech has made this option available to you. Your notice should
            be sent, in writing, to OpenSpeech’s address which is set out at the
            beginning of these Terms.
          </p>

          <p className="mt-2 text-gray-600">
            OpenSpeech may at any time, terminate its legal agreement with you
            if:
          </p>

          <ul className="mt-2 list-inside list-disc text-gray-600">
            <li>
              you have breached any provision of the Terms (or have acted in
              manner which clearly shows that you do not intend to, or are
              unable to comply with the provisions of the Terms); or
            </li>
            <li>
              OpenSpeech is required to do so by law (for example, where the
              provision of the Services to you is, or becomes, unlawful); or
            </li>
            <li>
              the partner with whom OpenSpeech offered the Services to you has
              terminated its relationship with OpenSpeech or ceased to offer the
              Services to you; or
            </li>
            <li>
              OpenSpeech is transitioning to no longer providing the Services to
              users in the country in which you are resident or from which you
              use the service; or
            </li>
            <li>
              the provision of the Services to you by OpenSpeech is, in
              OpenSpeech’s opinion, no longer commercially viable.
            </li>
          </ul>

          <p className="mt-2 text-gray-600">
            Nothing in this Section shall affect OpenSpeech’s rights regarding
            provision of Services under Section 4 of the Terms.
          </p>

          <p className="mt-2 text-gray-600">
            When these Terms come to an end, all of the legal rights,
            obligations and liabilities that you and OpenSpeech have benefited
            from, been subject to (or which have accrued over time whilst the
            Terms have been in force) or which are expressed to continue
            indefinitely, shall be unaffected by this cessation, and the
            provisions of paragraph 19.7 shall continue to apply to such rights,
            obligations and liabilities indefinitely.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            EXCLUSION OF WARRANTIES
          </h2>

          <p className="mt-2 text-gray-600">
            NOTHING IN THESE TERMS, INCLUDING SECTIONS 13 AND 14, SHALL EXCLUDE
            OR LIMIT OpenSpeech’S WARRANTY OR LIABILITY FOR LOSSES WHICH MAY NOT
            BE LAWFULLY EXCLUDED OR LIMITED BY APPLICABLE LAW. SOME
            JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR
            CONDITIONS OR THE LIMITATION OR EXCLUSION OF LIABILITY FOR LOSS OR
            DAMAGE CAUSED BY NEGLIGENCE, BREACH OF CONTRACT OR BREACH OF IMPLIED
            TERMS, OR INCIDENTAL OR CONSEQUENTIAL DAMAGES. ACCORDINGLY, ONLY THE
            LIMITATIONS WHICH ARE LAWFUL IN YOUR JURISDICTION WILL APPLY TO YOU
            AND OUR LIABILITY WILL BE LIMITED TO THE MAXIMUM EXTENT PERMITTED BY
            LAW.
          </p>

          <p className="mt-2 text-gray-600">
            YOU EXPRESSLY UNDERSTAND AND AGREE THAT YOUR USE OF THE SERVICES IS
            AT YOUR SOLE RISK AND THAT THE SERVICES ARE PROVIDED "AS IS" AND “AS
            AVAILABLE.”
          </p>

          <p className="mt-2 text-gray-600">
            IN PARTICULAR, OpenSpeech, ITS SUBSIDIARIES AND AFFILIATES, AND ITS
            LICENSORS DO NOT REPRESENT OR WARRANT TO YOU THAT:
          </p>

          <ul className="mt-2 list-inside list-disc text-gray-600">
            <li>YOUR USE OF THE SERVICES WILL MEET YOUR REQUIREMENTS,</li>
            <li>
              YOUR USE OF THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE OR
              FREE FROM ERROR,
            </li>
            <li>
              ANY INFORMATION OBTAINED BY YOU AS A RESULT OF YOUR USE OF THE
              SERVICES WILL BE ACCURATE OR RELIABLE, AND
            </li>
            <li>
              THAT DEFECTS IN THE OPERATION OR FUNCTIONALITY OF ANY SOFTWARE
              PROVIDED TO YOU AS PART OF THE SERVICES WILL BE CORRECTED.
            </li>
          </ul>

          <p className="mt-2 text-gray-600">
            ANY MATERIAL DOWNLOADED OR OTHERWISE OBTAINED THROUGH THE USE OF THE
            SERVICES IS DONE AT YOUR OWN DISCRETION AND RISK AND THAT YOU WILL
            BE SOLELY RESPONSIBLE FOR ANY DAMAGE TO YOUR COMPUTER SYSTEM OR
            OTHER DEVICE OR LOSS OF DATA THAT RESULTS FROM THE DOWNLOAD OF ANY
            SUCH MATERIAL.
          </p>

          <p className="mt-2 text-gray-600">
            NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY YOU
            FROM OpenSpeech OR THROUGH OR FROM THE SERVICES SHALL CREATE ANY
            WARRANTY NOT EXPRESSLY STATED IN THE TERMS.
          </p>

          <p className="mt-2 text-gray-600">
            OpenSpeech FURTHER EXPRESSLY DISCLAIMS ALL WARRANTIES AND CONDITIONS
            OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED
            TO THE IMPLIED WARRANTIES AND CONDITIONS OF MERCHANTABILITY, FITNESS
            FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            LIMITATION OF LIABILITY
          </h2>

          <p className="mt-2 text-gray-600">
            SUBJECT TO OVERALL PROVISION IN PARAGRAPH 13.1 ABOVE, YOU EXPRESSLY
            UNDERSTAND AND AGREE THAT OpenSpeech, ITS SUBSIDIARIES AND
            AFFILIATES, AND ITS LICENSORS SHALL NOT BE LIABLE TO YOU FOR:
          </p>

          <ul className="mt-2 list-inside list-disc text-gray-600">
            <li>
              ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL CONSEQUENTIAL OR
              EXEMPLARY DAMAGES WHICH MAY BE INCURRED BY YOU, HOWEVER CAUSED AND
              UNDER ANY THEORY OF LIABILITY. THIS SHALL INCLUDE, BUT NOT BE
              LIMITED TO, ANY LOSS OF PROFIT (WHETHER INCURRED DIRECTLY OR
              INDIRECTLY), ANY LOSS OF GOODWILL OR BUSINESS REPUTATION, ANY LOSS
              OF DATA SUFFERED, COST OF PROCUREMENT OF SUBSTITUTE GOODS OR
              SERVICES, OR OTHER INTANGIBLE LOSS;
            </li>
            <li>
              ANY LOSS OR DAMAGE WHICH MAY BE INCURRED BY YOU, INCLUDING BUT NOT
              LIMITED TO LOSS OR DAMAGE AS A RESULT OF:
            </li>
            <li>
              ANY RELIANCE PLACED BY YOU ON THE COMPLETENESS, ACCURACY OR
              EXISTENCE OF ANY ADVERTISING, OR AS A RESULT OF ANY RELATIONSHIP
              OR TRANSACTION BETWEEN YOU AND ANY ADVERTISER OR SPONSOR WHOSE
              ADVERTISING APPEARS ON THE SERVICES;
            </li>
            <li>
              ANY CHANGES WHICH OpenSpeech MAY MAKE TO THE SERVICES, OR FOR ANY
              PERMANENT OR TEMPORARY CESSATION IN THE PROVISION OF THE SERVICES
              (OR ANY FEATURES WITHIN THE SERVICES);
            </li>
            <li>
              THE DELETION OF, CORRUPTION OF, OR FAILURE TO STORE, ANY CONTENT
              AND OTHER COMMUNICATIONS DATA MAINTAINED OR TRANSMITTED BY OR
              THROUGH YOUR USE OF THE SERVICES;
            </li>
            <li>
              YOUR FAILURE TO PROVIDE OpenSpeech WITH ACCURATE ACCOUNT
              INFORMATION;
            </li>
            <li>
              YOUR FAILURE TO KEEP YOUR PASSWORD OR ACCOUNT DETAILS SECURE AND
              CONFIDENTIAL;
            </li>
          </ul>

          <p className="mt-2 text-gray-600">
            THE LIMITATIONS ON OpenSpeech’S LIABILITY TO YOU IN PARAGRAPH 14.1
            ABOVE SHALL APPLY WHETHER OR NOT OpenSpeech HAS BEEN ADVISED OF OR
            SHOULD HAVE BEEN AWARE OF THE POSSIBILITY OF ANY SUCH LOSSES
            ARISING.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Advertisements
          </h2>

          <p className="mt-2 text-gray-600">
            Some of the Services are supported by advertising revenue and may
            display advertisements and promotions. These advertisements may be
            targeted to the content of information stored on the Services,
            queries made through the Services or other information.
          </p>

          <p className="mt-2 text-gray-600">
            The manner, mode and extent of advertising by OpenSpeech on the
            Services are subject to change without specific notice to you.
          </p>

          <p className="mt-2 text-gray-600">
            In consideration for OpenSpeech granting you access to and use of
            the Services, you agree that OpenSpeech may place such advertising
            on the Services.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Other content
          </h2>

          <p className="mt-2 text-gray-600">
            The Services may include hyperlinks to other web sites or content or
            resources. OpenSpeech may have no control over any web sites or
            resources which are provided by companies or persons other than
            OpenSpeech.
          </p>

          <p className="mt-2 text-gray-600">
            You acknowledge and agree that OpenSpeech is not responsible for the
            availability of any such external sites or resources, and does not
            endorse any advertising, products or other materials on or available
            from such web sites or resources.
          </p>

          <p className="mt-2 text-gray-600">
            You acknowledge and agree that OpenSpeech is not liable for any loss
            or damage which may be incurred by you as a result of the
            availability of those external sites or resources, or as a result of
            any reliance placed by you on the completeness, accuracy or
            existence of any advertising, products or other materials on, or
            available from, such web sites or resources.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Changes to the Terms
          </h2>

          <p className="mt-2 text-gray-600">
            OpenSpeech may make changes to the Universal Terms or Additional
            Terms from time to time. When these changes are made, OpenSpeech
            will make a new copy of the Universal Terms available at{' '}
            <a
              href="https://www.openspeech.io/terms"
              className="text-blue-500 hover:text-blue-700"
            >
              https://www.openspeech.io/terms
            </a>{' '}
            and any new Additional Terms will be made available to you from
            within, or through, the affected Services.
          </p>

          <p className="mt-2 text-gray-600">
            You understand and agree that if you use the Services after the date
            on which the Universal Terms or Additional Terms have changed,
            OpenSpeech will treat your use as acceptance of the updated
            Universal Terms or Additional Terms.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            General legal terms
          </h2>

          <p className="mt-2 text-gray-600">
            Sometimes when you use the Services, you may (as a result of, or
            through your use of the Services) use a service or download a piece
            of software, or purchase goods, which are provided by another person
            or company. Your use of these other services, software or goods may
            be subject to separate terms between you and the company or person
            concerned. If so, the Terms do not affect your legal relationship
            with these other companies or individuals.
          </p>

          <p className="mt-2 text-gray-600">
            The Terms constitute the whole legal agreement between you and
            OpenSpeech and govern your use of the Services (but excluding any
            services which OpenSpeech may provide to you under a separate
            written agreement), and completely replace any prior agreements
            between you and OpenSpeech in relation to the Services.
          </p>

          <p className="mt-2 text-gray-600">
            You agree that OpenSpeech may provide you with notices, including
            those regarding changes to the Terms, by email, regular mail, or
            postings on the Services.
          </p>

          <p className="mt-2 text-gray-600">
            You agree that if OpenSpeech does not exercise or enforce any legal
            right or remedy which is contained in the Terms (or which OpenSpeech
            has the benefit of under any applicable law), this will not be taken
            to be a formal waiver of OpenSpeech’s rights and that those rights
            or remedies will still be available to OpenSpeech.
          </p>

          <p className="mt-2 text-gray-600">
            If any court of law, having the jurisdiction to decide on this
            matter, rules that any provision of these Terms is invalid, then
            that provision will be removed from the Terms without affecting the
            rest of the Terms. The remaining provisions of the Terms will
            continue to be valid and enforceable.
          </p>

          <p className="mt-2 text-gray-600">
            You acknowledge and agree that each member of the group of companies
            of which OpenSpeech is the parent shall be third party beneficiaries
            to the Terms and that such other companies shall be entitled to
            directly enforce, and rely upon, any provision of the Terms which
            confers a benefit on (or rights in favor of) them. Other than this,
            no other person or company shall be third party beneficiaries to the
            Terms.
          </p>

          <p className="mt-2 text-gray-600">
            The Terms, and your relationship with OpenSpeech under the Terms,
            shall be governed by the laws of the State of California without
            regard to its conflict of laws provisions. You and OpenSpeech agree
            to submit to the exclusive jurisdiction of the courts located within
            the county of Santa Clara, California to resolve any legal matter
            arising from the Terms. Notwithstanding this, you agree that
            OpenSpeech shall still be allowed to apply for injunctive remedies
            (or an equivalent type of urgent legal relief) in any jurisdiction.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Open Source Software
          </h2>

          <p className="mt-2 text-gray-600">
            Certain items of software included with the Services are subject to
            “open source” or “free software” licenses (“Open Source Software”).
            Some of the Open Source Software is owned by third parties. The Open
            Source Software is not subject to the terms and conditions of
            Section 9.1 (General) but instead is licensed under the terms of the
            end user license that accompanies such Open Source Software. Nothing
            in these Terms limits your rights under, or grants you rights that
            supersede, the terms and conditions of any applicable end user
            license for the Open Source Software.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Language of the Terms
          </h2>

          <p className="mt-2 text-gray-600">
            Where OpenSpeech has provided you with a translation of the English
            language version of the Terms, then you agree that the translation
            is provided for your convenience only and that the English language
            versions of the Terms will govern your relationship with OpenSpeech.
          </p>

          <p className="mt-2 text-gray-600">
            If there is any contradiction between what the English language
            version of the Terms says and what a translation says, then the
            English language version shall take precedence.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Intellectual Property Rights
          </h2>

          <p className="mt-2 text-gray-600">
            OpenSpeech reserves all of its intellectual property rights in the
            Services. Using the Services does not give you any ownership in our
            Services or the content or information made available through our
            Services. Trademarks and logos used in connection with the Services
            are the trademarks of their respective owners. OpenSpeech, and other
            OpenSpeech trademarks, service marks, graphics, and logos used for
            our Services are trademarks or registered trademarks of OpenSpeech.
            Other than as expressly permitted by applicable law, you may not
            copy, modify, distribute, sell, or lease any part of our Services or
            included software, nor may you reverse engineer or attempt to
            extract the source code of that software, unless laws prohibit those
            restrictions or you have our written permission.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            Payment Terms
          </h2>

          <p className="mt-2 text-gray-600">
            Eligible first-time subscribers may receive a seven-day free trial
            after selecting a paid plan and providing a valid payment method.
            Unless you cancel before the trial ends, your paid subscription will
            begin automatically and the payment method will be charged the price
            and billing interval shown when you subscribed.
          </p>

          <p className="mt-2 text-gray-600">
            Paid subscriptions renew automatically at the selected billing
            interval until canceled. You can schedule cancellation from your
            account settings; access continues until the end of the current
            trial or paid billing period, after which no further renewal is
            charged. You can remove a scheduled cancellation before that period
            ends. Once a subscription has ended, you must choose a plan again to
            resubscribe.
          </p>

          <p className="mt-2 text-gray-600">
            You agree to pay the displayed fees, applicable taxes, and any
            foreign-exchange or payment-provider charges associated with your
            purchase. Available payment methods and plan prices may change, but
            any new price will be disclosed before it applies as required by
            applicable law.
          </p>

          <h2 className="mt-8 text-2xl font-bold tracking-tight">
            About these Terms
          </h2>

          <p className="mt-2 text-gray-600">
            These terms control the relationship between OpenSpeech and you.
            They do not create any third party beneficiary rights.
          </p>

          <p className="mt-2 text-gray-600">
            If you do not comply with these terms, and we don’t take action
            right away, this doesn’t mean that we are giving up any rights that
            we may have (such as taking action in the future).
          </p>

          <p className="mt-2 text-gray-600">
            If it turns out that a particular term is not enforceable, this will
            not affect any other terms.
          </p>

          <p className="mt-2 text-gray-600">
            The laws of California, U.S.A., excluding California’s conflict of
            laws rules, will apply to any disputes arising out of or relating to
            these terms or the Services. All claims arising out of or relating
            to these terms or the Services will be litigated exclusively in the
            federal or state courts of Santa Clara County, California, USA, and
            you and OpenSpeech consent to personal jurisdiction in those courts.
          </p>

          <p className="mt-2 text-gray-600">
            For information about how to contact OpenSpeech, please visit our
            contact page.
          </p>
        </div>
      </div>
    </>
  )
}
